import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { createAttachments } from "../../../../../services/apiBoards";
import useMutate from "../../../../../hooks/useMutate";

const INITIAL_ATTACHMENT_NAME = [{ name: "" }];
const INITIAL_ATTACHMENT_FILE = [{ file: null }];

export function useBoardAttachmentForm({ boardId, toggleDrawer, userId }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(INITIAL_ATTACHMENT_NAME);
  const [file, setFile] = useState(INITIAL_ATTACHMENT_FILE);

  const resetState = useCallback(() => {
    setName(INITIAL_ATTACHMENT_NAME);
    setFile(INITIAL_ATTACHMENT_FILE);
  }, []);

  const resetFileInput = useCallback(() => {
    const target = document.getElementById("input-file-field");

    if (target) {
      target.value = "";
    }
  }, []);

  const { mutate: createAttachmentMutate, isPending } = useMutate(
    createAttachments,
    [],
    () => {},
    () => {
      queryClient.invalidateQueries({
        queryKey: [`attachments-${boardId}`],
      });
      resetState();
      resetFileInput();
    },
    () => {}
  );

  const handleNameChange = useCallback((event, index) => {
    const nextNames = [...name];
    nextNames[index] = { name: event.target.value };
    setName(nextNames);
  }, [name]);

  const handleFileChange = useCallback((event) => {
    if (event.target.files.length === 0) {
      resetState();
      return;
    }

    setFile(
      Array.from(event.target.files).map((selectedFile) => ({
        file: selectedFile,
      }))
    );
  }, [resetState]);

  const selectedFile = useMemo(() => file[0]?.file ?? null, [file]);
  const canSubmit = useMemo(() => Boolean(selectedFile), [selectedFile]);

  const handleSubmit = useCallback(() => {
    const formData = file.map((item, index) => ({
      name: name[index]?.name ?? item.file?.name,
      file: item.file,
    }));

    const firstFile = formData[0]?.file;

    if (!firstFile?.name) {
      toast.error("Please upload file");
      return;
    }

    const attachmentPayload = new FormData();
    attachmentPayload.append("board_id", boardId);
    attachmentPayload.append("user_id", userId);
    attachmentPayload.append(
      "names",
      formData.map((item) => item.name).join(", ")
    );

    formData.forEach((item) => {
      attachmentPayload.append("files", item.file);
    });

    createAttachmentMutate(attachmentPayload);
  }, [boardId, createAttachmentMutate, file, name, userId]);

  const handleDrawerClose = useCallback((anchor) => {
    resetState();
    resetFileInput();
    toggleDrawer(anchor, false)();
  }, [resetFileInput, resetState, toggleDrawer]);

  return {
    canSubmit,
    file,
    handleDrawerClose,
    handleFileChange,
    handleNameChange,
    handleSubmit,
    isPending,
    name,
  };
}
